import * as XLSX from 'xlsx';

interface IBalance {
  id: number;
  opening_balance: number;
  end_balance: number;
  age_description: string;
  description: string;
  name: string;
  birth: number;
  internal_in_transfer: number;
  internal_out_transfer: number;
  shopping: number;
  sales: number;
  external_in_transfer: number;
  external_out_transfer: number;
  death: number;
  evolution: number;
  inventory_adjustment: number;
}

interface IBalanceAnimal {
  category_id: number;
  age_description: string;
  name: string;
  type: string;
  quantity: number;
  average_weight: number;
  total_weight: number;
  total_arroba: number;
  total_uas: number;
}

interface IBalanceCapacity {
  category_id: number;
  age_description: string;
  name: string;
  batch_id: number;
  initials: string;
  month_reference: number;
  quantity: number;
  total_area: number;
  total_capacity: number;
  average_weight: number;
  batch_capacity_rate: number;
  capacity: number;
  ua_total: number;
  rain: number;
}

export const exportCattleMapDataToExcel = (
  balance: IBalance[],
  balanceAnimal: IBalanceAnimal[],
  balanceCapacityMap: Map<number, IBalanceCapacity[]>,
  startDate?: Date,
  endDate?: Date
) => {
  if (!balance || !balanceAnimal || !balanceCapacityMap) {
    throw new Error('Dados insuficientes para exportação');
  }

  const workbook = XLSX.utils.book_new();

  const balanceData = balance.sort((a, b) => a.id - b.id).map((item) => ({
    'Categoria Animal': `${item.name} ${item.age_description.toLowerCase()}`,
    'Saldo Inicial': item.opening_balance,
    'Nasc.': item.birth,
    'Transf. Interna (Entrada)': item.internal_in_transfer,
    'Transf. Externa (Entrada)': item.external_in_transfer,
    'Compras': item.shopping,
    'Venda': item.sales,
    'Transf. Interna (Saída)': item.internal_out_transfer,
    'Transf. Externa (Saída)': item.external_out_transfer,
    'Morte': item.death,
    'Evolução': item.evolution,
    'Ajuste no período': item.inventory_adjustment,
    'Saldo': item.end_balance
  }));

  const totalRow = {
    'Categoria Animal': 'Total',
    'Saldo Inicial': balance.reduce((acc, item) => acc + item.opening_balance, 0),
    'Nasc.': balance.reduce((acc, item) => acc + item.birth, 0),
    'Transf. Interna (Entrada)': balance.reduce((acc, item) => acc + item.internal_in_transfer, 0),
    'Transf. Externa (Entrada)': balance.reduce((acc, item) => acc + item.external_in_transfer, 0),
    'Compras': balance.reduce((acc, item) => acc + item.shopping, 0),
    'Venda': balance.reduce((acc, item) => acc + item.sales, 0),
    'Transf. Interna (Saída)': balance.reduce((acc, item) => acc + item.internal_out_transfer, 0),
    'Transf. Externa (Saída)': balance.reduce((acc, item) => acc + item.external_out_transfer, 0),
    'Morte': balance.reduce((acc, item) => acc + item.death, 0),
    'Evolução': balance.reduce((acc, item) => acc + item.evolution, 0),
    'Ajuste no período': balance.reduce((acc, item) => acc + item.inventory_adjustment, 0),
    'Saldo': balance.reduce((acc, item) => acc + item.end_balance, 0)
  };

  const balanceWorksheet = XLSX.utils.json_to_sheet([...balanceData, totalRow]);
  balanceWorksheet['!cols'] = [
    { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 15 },
    { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 },
    { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 12 }
  ];
  XLSX.utils.book_append_sheet(workbook, balanceWorksheet, 'Balanço');

  const processedAnimals = balanceAnimal
    .reduce((acc: IBalanceAnimal[], item) => {
      const existingCategory = acc.find(cat => cat.category_id === item.category_id);
      if (existingCategory) {
        existingCategory.quantity += item.quantity;
        existingCategory.total_weight += item.total_weight;
        existingCategory.total_arroba += item.total_arroba;
        existingCategory.total_uas += item.total_uas;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [])
    .sort((a, b) => a.category_id - b.category_id);

  const totalUAs = processedAnimals.reduce((acc, item) => acc + item.total_uas, 0);

  const animalData = processedAnimals.map((item) => ({
    'Categoria Animal': `${item.name} ${item.age_description.toLowerCase()}`,
    'Estoque final': item.quantity,
    'Peso médio (Kg)': parseFloat(item.average_weight.toFixed(0)),
    'Peso total (Kg)': item.total_weight,
    'Total (@s)': parseFloat(item.total_arroba.toFixed(2)),
    'Total (UAs)': parseFloat(item.total_uas.toFixed(2)),
    'Distr. (UAs)': `${parseFloat((item.total_uas / totalUAs * 100).toFixed(2))}%`
  }));

  const animalTotalRow = {
    'Categoria Animal': 'Total',
    'Estoque final': processedAnimals.reduce((acc, item) => acc + item.quantity, 0),
    'Peso médio (Kg)': '',
    'Peso total (Kg)': processedAnimals.reduce((acc, item) => acc + item.total_weight, 0),
    'Total (@s)': parseFloat(processedAnimals.reduce((acc, item) => acc + item.total_arroba, 0).toFixed(2)),
    'Total (UAs)': parseFloat(processedAnimals.reduce((acc, item) => acc + item.total_uas, 0).toFixed(2)),
    'Distr. (UAs)': ''
  };

  const animalWorksheet = XLSX.utils.json_to_sheet([...animalData, animalTotalRow]);
  animalWorksheet['!cols'] = [
    { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
    { wch: 15 }, { wch: 15 }, { wch: 15 }
  ];
  XLSX.utils.book_append_sheet(workbook, animalWorksheet, 'Categoria Animal');

  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  
  const capacityRows: any[][] = [
    ['Lotação/Mês', ...months.map((_, idx) => idx + 1)]
  ];

  const avgAnimalsRow: any[] = ['Nº médio de Animais'];
  for (let i = 0; i < 12; i++) {
    const monthData = balanceCapacityMap.get(i) || [];
    const avg = monthData.length > 0 
      ? monthData.reduce((acc, item) => acc + item.quantity, 0)
      : '';
    avgAnimalsRow.push(avg);
  }
  capacityRows.push(avgAnimalsRow);

  const avgCapacityRow: any[] = ['Lotação média (UAs)'];
  for (let i = 0; i < 12; i++) {
    const monthData = balanceCapacityMap.get(i) || [];
    if (monthData.length > 0) {
      const totalArea = monthData.reduce((acc, item) => acc + item.total_area, 0);
      const totalUAs = monthData.reduce((acc, item) => acc + item.ua_total, 0);
      const capacity = totalArea > 0 ? parseFloat((totalUAs / totalArea).toFixed(2)) : '';
      avgCapacityRow.push(capacity);
    } else {
      avgCapacityRow.push('');
    }
  }
  capacityRows.push(avgCapacityRow);

  const capacityWorksheet = XLSX.utils.aoa_to_sheet(capacityRows);
  capacityWorksheet['!cols'] = [
    { wch: 20 },
    ...Array(12).fill({ wch: 12 })
  ];
  XLSX.utils.book_append_sheet(workbook, capacityWorksheet, 'Curva Estoque');

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  let filename = 'mapa-gado';
  if (startDate && endDate) {
    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];
    filename = `mapa-gado-${startStr}-${endStr}.xlsx`;
  } else {
    filename = `mapa-gado-${new Date().toISOString().split('T')[0]}.xlsx`;
  }
  
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
